import { useReducer, useMemo, useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Minimize2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DialogRoot,
  DialogContent,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { WorkspacePanel } from '@genieartinc/studio-ui'

import { describeSubmitFailure } from './create-model/submit-errors'
import type { ModelMode } from './create-model/types'
import { createModelReducer, initialState } from './create-model/reducer'
import { CreateModelContext } from './create-model/context'
import { getModelById } from './create-model/constants'
import { groupValidationWarnings, calculateCost, canSubmitTraining } from './create-model/validation'
import { TrainMode } from './create-model/TrainMode'
import { ConfigPanel } from './create-model/ConfigPanel'
import { ImportMode as ImportModeContent } from './create-model/ImportMode'
import { SubmitProgress } from './create-model/SubmitProgress'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { trainingAPI } from '@/lib/api/training'
import { useTrainingWidgetStore, type TrainingWidgetStatus } from '@/stores/useTrainingWidgetStore'

async function buildThumbnailDataUrl(file: File, maxSize = 400): Promise<string | undefined> {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height))
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close?.()
    return canvas.toDataURL('image/jpeg', 0.8)
  } catch {
    return undefined
  }
}

// ─── Main Dialog ────────────────────────────────────────────────

interface CreateModelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Dock slot (the Library's): renders as a workspace panel there instead of a modal. */
  host?: HTMLElement | null
}

export function CreateModelDialog({ open, onOpenChange, host }: CreateModelDialogProps) {
  const [state, dispatch] = useReducer(createModelReducer, initialState)
  const [minimized, setMinimized] = useState(false)

  // Clean up object URLs when dialog closes (only if not submitting)
  useEffect(() => {
    if (!open && !minimized && state.submitStatus === 'idle') {
      if (state.images.length > 0) dispatch({ type: 'RESET' })
    }
  }, [open, minimized]) // eslint-disable-line react-hooks/exhaustive-deps

  // Real wallet balance
  const { balance } = useWalletBalance()
  const walletBalance = balance ? parseFloat(balance) : 0

  // Derived values
  const selectedTrainer = useMemo(
    () => (state.selectedTrainerId ? getModelById(state.selectedTrainerId) ?? null : null),
    [state.selectedTrainerId],
  )

  const validationWarnings = useMemo(
    () => groupValidationWarnings(state.images, dispatch),
    [state.images],
  )

  const estimatedCost = useMemo(
    () => (selectedTrainer ? calculateCost(selectedTrainer, state.steps, walletBalance) : null),
    [selectedTrainer, state.steps, walletBalance],
  )

  const canSubmit = useMemo(
    () => canSubmitTraining(state, selectedTrainer, walletBalance),
    [state, selectedTrainer, walletBalance],
  )

  const contextValue = useMemo(
    () => ({ state, dispatch, validationWarnings, estimatedCost, canSubmit, selectedTrainer }),
    [state, dispatch, validationWarnings, estimatedCost, canSubmit, selectedTrainer],
  )

  const isSubmitting = state.submitStatus !== 'idle' && state.submitStatus !== 'error'
  const isSubmitted = state.submitStatus === 'submitted'
  const isTrainMode = state.mode === 'train'

  // Real submit: build ZIP → upload to fal.ai → submit training job
  const handleSubmit = useCallback(async () => {
    if (!isTrainMode || !selectedTrainer) {
      if (!isTrainMode) onOpenChange(false)
      return
    }

    try {
      const t0 = performance.now()

      // Step 1: Build ZIP from images + captions
      console.log('[Training] Step 1/3: Building ZIP...', {
        images: state.images.length,
        trainer: selectedTrainer.falEndpoint,
        steps: state.steps,
        modelName: state.modelName,
        walletBalance,
        estimatedCost: estimatedCost?.totalCost,
      })
      dispatch({ type: 'SET_SUBMIT_STATUS', payload: { status: 'preparing' } })
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()

      for (const img of state.images) {
        zip.file(img.name, img.file)
        if (img.caption) {
          const baseName = img.name.replace(/\.[^.]+$/, '')
          zip.file(`${baseName}.txt`, img.caption)
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const t1 = performance.now()
      console.log(`[Training] Step 1/3 DONE (${Math.round(t1 - t0)}ms): ZIP built`, {
        sizeBytes: zipBlob.size,
        sizeMB: (zipBlob.size / 1024 / 1024).toFixed(1),
        files: Object.keys(zip.files).length,
      })

      // Step 2: Upload ZIP with real progress tracking
      console.log('[Training] Step 2/3: Uploading ZIP to fal.ai...')
      dispatch({ type: 'SET_SUBMIT_STATUS', payload: { status: 'uploading' } })
      const uploadResult = await trainingAPI.uploadDataset(
        zipBlob,
        `${(state.modelName || 'dataset').replace(/\s+/g, '_')}.zip`,
        (pct) => dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: pct }),
      )
      const t2 = performance.now()
      console.log(`[Training] Step 2/3 DONE (${Math.round(t2 - t1)}ms): Upload complete`, uploadResult)

      // Step 3: Submit training job
      const config: Record<string, unknown> = {
        steps: state.steps,
        learning_rate: state.learningRate,
        trigger_word: state.triggerWord || undefined,
        is_style: state.modelType === 'style',
        ...state.advancedSettings,
      }

      const firstImage = state.images[0]
      const thumbnailUrl = firstImage ? await buildThumbnailDataUrl(firstImage.file) : undefined

      const submitPayload = {
        modelEndpointId: selectedTrainer.falEndpoint,
        name: state.modelName,
        datasetUrl: uploadResult.url,
        config,
        datasetMeta: {
          imageCount: state.images.length,
          totalSizeBytes: uploadResult.size,
          captionCount: state.images.filter(i => i.caption).length,
          triggerWord: state.triggerWord || undefined,
          thumbnailUrl,
        },
      }
      console.log('[Training] Step 3/3: Submitting job...', submitPayload)

      const jobResult = await trainingAPI.submitJob(submitPayload)
      const t3 = performance.now()
      console.log(`[Training] Step 3/3 DONE (${Math.round(t3 - t2)}ms): Job submitted!`, jobResult)
      console.log(`[Training] Total time: ${Math.round(t3 - t0)}ms`)

      dispatch({ type: 'SET_SUBMIT_STATUS', payload: { status: 'submitted' } })

      // Notify sidebar models list to refetch
      window.dispatchEvent(new Event('training-job-created'))
    } catch (error) {
      // An out-of-credits refusal is an expected answer, not a crash: it gets the wallet's
      // own message and no console noise. Everything else keeps the rich debug output.
      const failure = describeSubmitFailure(error)
      if (!failure.insufficientBalance) {
        const errObj = error instanceof Error ? error : new Error(String(error))
        console.error('[Training] Submit failed:', {
          message: errObj.message,
          name: errObj.name,
          stack: errObj.stack?.split('\n').slice(0, 5).join('\n'),
        })
      }

      dispatch({
        type: 'SET_SUBMIT_STATUS',
        payload: { status: 'error', error: failure.message, errorTitle: failure.title },
      })
    }
  }, [isTrainMode, selectedTrainer, state.images, state.modelName, state.steps, state.learningRate, state.triggerWord, state.modelType, state.advancedSettings, dispatch, onOpenChange])

  const showWidget = useTrainingWidgetStore((s) => s.show)
  const updateWidget = useTrainingWidgetStore((s) => s.update)
  const hideWidget = useTrainingWidgetStore((s) => s.hide)

  const handleClose = useCallback(() => {
    onOpenChange(false)
    setMinimized(false)
    hideWidget()
    if (isSubmitted) dispatch({ type: 'RESET' })
  }, [onOpenChange, isSubmitted, hideWidget])

  const handleMaximize = useCallback(() => {
    setMinimized(false)
    hideWidget()
    onOpenChange(true)
  }, [onOpenChange, hideWidget])

  const handleDismissWidget = useCallback(() => {
    setMinimized(false)
    hideWidget()
    dispatch({ type: 'RESET' })
  }, [hideWidget])

  const handleMinimize = useCallback(() => {
    if (isSubmitting) {
      showWidget({
        modelName: state.modelName,
        status: state.submitStatus as TrainingWidgetStatus,
        uploadProgress: state.uploadProgress,
        onMaximize: handleMaximize,
        onDismiss: handleDismissWidget,
      })
    }
    setMinimized(true)
    onOpenChange(false)
  }, [isSubmitting, state.modelName, state.submitStatus, state.uploadProgress, showWidget, handleMaximize, handleDismissWidget, onOpenChange])

  // Keep widget in sync with submission progress while minimized
  useEffect(() => {
    if (minimized && isSubmitting) {
      updateWidget({
        status: state.submitStatus as TrainingWidgetStatus,
        uploadProgress: state.uploadProgress,
        modelName: state.modelName,
      })
    }
  }, [minimized, isSubmitting, state.submitStatus, state.uploadProgress, state.modelName, updateWidget])

  // Hide widget if submission ends while not minimized
  useEffect(() => {
    if (!minimized) hideWidget()
  }, [minimized, hideWidget])

  // CTA label
  const ctaLabel = (() => {
    if (state.submitStatus === 'preparing') return 'Preparing...'
    if (state.submitStatus === 'uploading') return 'Uploading...'
    if (state.submitStatus === 'submitted') return 'Done'
    if (!isTrainMode) return 'Save model'
    if (estimatedCost) return `Train — $${estimatedCost.totalCost.toFixed(2)}`
    return 'Train model'
  })()


  const modeTabs = (
    <Tabs value={state.mode} onValueChange={(v) => dispatch({ type: 'SET_MODE', payload: v as ModelMode })}>
      <TabsList className="h-8">
        <TabsTrigger value="train" className="text-xs">Train</TabsTrigger>
        <TabsTrigger value="import" className="text-xs">Import</TabsTrigger>
      </TabsList>
    </Tabs>
  )
  const trainFooter = (
    <div className="border-t px-5 py-3">
      {state.submitError && (
        <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 p-2">
          <p className="text-xs font-medium text-destructive">Training failed</p>
          <p className="mt-0.5 text-[11px] text-destructive/80">{state.submitError}</p>
          <button className="mt-1 text-[10px] text-destructive/60 underline hover:text-destructive" onClick={() => { navigator.clipboard.writeText(state.submitError || '') }}>Copy error</button>
        </div>
      )}
      <Button className="w-full" size="lg" disabled={!canSubmit} onClick={handleSubmit}>{ctaLabel}</Button>
    </div>
  )

  // Docked: one scrolling column in the Library's slot, the action pinned at the bottom.
  if (host) {
    if (!open) return null
    const panel = (
      <WorkspacePanel title="Create model" onClose={isSubmitting && !isSubmitted ? handleMinimize : handleClose} className="!w-full h-full flex-1">
        <CreateModelContext.Provider value={contextValue}>
          {isSubmitting ? (
            /* ── Submission Progress (centered, no min-h gap) ── */
            <div className="flex min-h-[400px] flex-1 flex-col">
              <div className="flex flex-1 items-center justify-center">
                <div className="w-full max-w-lg">
                  <SubmitProgress />
                </div>
              </div>
              <div className="flex items-center justify-between border-t px-6 py-3">
                {!isSubmitted && (
                  <Button variant="ghost" size="sm" onClick={handleMinimize}>
                    <Minimize2 className="mr-2 h-3.5 w-3.5" />
                    Minimize
                  </Button>
                )}
                <div className="flex-1" />
                {isSubmitted ? (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="lg" onClick={handleMinimize}>Minimize</Button>
                    <Button size="lg" onClick={handleClose}>Done</Button>
                  </div>
                ) : (
                  <Button size="lg" disabled>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {ctaLabel}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            // The panel body scrolls as a block; fill its height so only the form scrolls and the action stays put.
            <div className="flex h-full min-h-0 flex-col border-t border-border">
              <div className="flex items-center justify-between gap-3 px-5 py-3">
                <p className="text-sm text-muted-foreground">Models keep your art consistent</p>
                {modeTabs}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="border-b border-border px-5 py-3">
                      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Model name</label>
                      <Input
                        placeholder="e.g. My Portrait Style"
                        value={state.modelName}
                        onChange={(e) => dispatch({ type: 'SET_MODEL_NAME', payload: e.target.value })}
                        className="h-9 text-sm"
                      />
                    </div>
                {isTrainMode ? (
                  <>
                    <div className="p-5"><TrainMode /></div>
                    <div className="border-t border-border p-5"><ConfigPanel /></div>
                  </>
                ) : (
                  <div className="p-5"><ImportModeContent /></div>
                )}
              </div>
              {isTrainMode ? trainFooter : (
                <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
                  <Button variant="outline" size="lg" onClick={handleClose}>Cancel</Button>
                  <Button size="lg" disabled={!state.modelName || !state.importUrl} onClick={handleClose}>Save model</Button>
                </div>
              )}
            </div>
          )}
        </CreateModelContext.Provider>
      </WorkspacePanel>
    )
    return createPortal(panel, host)
  }

  return (
    <>
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="xl"
        className={cn(
          'flex max-w-[1100px] flex-col gap-0 overflow-hidden p-0',
          isSubmitting ? 'max-h-[85vh]' : 'max-h-[85vh] min-h-[600px]',
        )}
        showCloseButton={!isSubmitting}
      >
        <CreateModelContext.Provider value={contextValue}>
          {isSubmitting ? (
            /* ── Submission Progress (centered, no min-h gap) ── */
            <div className="flex min-h-[400px] flex-1 flex-col">
              <div className="flex flex-1 items-center justify-center">
                <div className="w-full max-w-lg">
                  <SubmitProgress />
                </div>
              </div>
              <div className="flex items-center justify-between border-t px-6 py-3">
                {!isSubmitted && (
                  <Button variant="ghost" size="sm" onClick={handleMinimize}>
                    <Minimize2 className="mr-2 h-3.5 w-3.5" />
                    Minimize
                  </Button>
                )}
                <div className="flex-1" />
                {isSubmitted ? (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="lg" onClick={handleMinimize}>Minimize</Button>
                    <Button size="lg" onClick={handleClose}>Done</Button>
                  </div>
                ) : (
                  <Button size="lg" disabled>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {ctaLabel}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            /* ── Two-Panel Layout ── */
            <>
              {/* Header */}
              <div className="flex items-center justify-between border-b px-6 py-4">
                <div className="flex items-center gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">Create the model</h2>
                    <p className="text-sm text-muted-foreground">Models keep your art consistent</p>
                  </div>
                </div>
                <Tabs
                  value={state.mode}
                  onValueChange={(v) => dispatch({ type: 'SET_MODE', payload: v as ModelMode })}
                >
                  <TabsList className="h-8">
                    <TabsTrigger value="train" className="text-xs">Train</TabsTrigger>
                    <TabsTrigger value="import" className="text-xs">Import</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {isTrainMode ? (
                /* ── Train: two panels ── */
                <div className="flex min-h-0 flex-1">
                  {/* Left Panel: Dataset */}
                  <div className="flex min-h-0 min-w-0 flex-[3] flex-col border-r border-border">
                    {/* Model name */}
                    <div className="border-b border-border px-5 py-3">
                      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Model name</label>
                      <Input
                        placeholder="e.g. My Portrait Style"
                        value={state.modelName}
                        onChange={(e) => dispatch({ type: 'SET_MODEL_NAME', payload: e.target.value })}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Scrollable dataset area */}
                    <div className="min-h-0 flex-1 overflow-y-auto p-5">
                      <TrainMode />
                    </div>
                  </div>

                  {/* Right Panel: Config */}
                  <div className="flex min-h-0 w-[320px] shrink-0 flex-col">
                    <div className="min-h-0 flex-1 overflow-y-auto p-5">
                      <ConfigPanel />
                    </div>

                    {/* Footer / CTA */}
                    <div className="border-t px-5 py-3">
                      {state.submitError && (
                        <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 p-2">
                          <p className="text-xs font-medium text-destructive">{state.submitErrorTitle ?? 'Training failed'}</p>
                          <p className="mt-0.5 text-[11px] text-destructive/80">{state.submitError}</p>
                          <button
                            className="mt-1 text-[10px] text-destructive/60 underline hover:text-destructive"
                            onClick={() => { navigator.clipboard.writeText(state.submitError || ''); }}
                          >
                            Copy error
                          </button>
                        </div>
                      )}
                      <Button
                        className="w-full"
                        size="lg"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                      >
                        {ctaLabel}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── Import: single panel ── */
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="min-h-0 flex-1 overflow-y-auto p-6">
                    {/* Model name */}
                    <div className="mb-4 flex flex-col gap-2">
                      <label className="text-sm font-medium">Model name</label>
                      <Input
                        placeholder="My model"
                        value={state.modelName}
                        onChange={(e) => dispatch({ type: 'SET_MODEL_NAME', payload: e.target.value })}
                      />
                    </div>
                    <ImportModeContent />
                  </div>
                  <div className="flex items-center justify-end gap-2 border-t px-6 py-3">
                    <Button variant="outline" size="lg" onClick={handleClose}>Cancel</Button>
                    <Button size="lg" disabled={!state.modelName || !state.importUrl} onClick={handleClose}>
                      Save model
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CreateModelContext.Provider>
      </DialogContent>
    </DialogRoot>
    </>
  )
}
